import { useEffect, useState } from 'react';

/**
 * @file THE FADE: one full-screen black rectangle whose opacity the engine
 * drives, for the blink between the street and a building's interior.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BUS CONTRACT — `'screen:fade'`
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 * bus.emit('screen:fade', { black: true,  ms: 220 })   // out, to black
 * bus.emit('screen:fade', { black: false, ms: 260 })   // back in
 * ```
 * ⚠ THE TIMING IS THE EMITTER'S, NOT OURS. The teleport happens at the bottom
 * of the fade, so the system that moves the player is the only thing that can
 * know when the screen is black — it therefore owns the clock and sends the two
 * halves itself. There is no `setTimeout` in this file at all: it obeys the
 * payload and nothing else, which is what makes the sequence impossible to
 * desynchronise from the sim.
 *
 * ⚠ THE CHANNEL IS SUBSCRIBED DIRECTLY, not through `useEngineBus` — the same
 * trap `Wasted.jsx` records. That hook throttles and keeps only the last
 * payload, so a short fade-out immediately answered by a fade-in would be
 * coalesced into one event and the screen would stay black forever.
 *
 * ⚠ AND A TEARDOWN CLEARS IT. A level disposed mid-fade has nothing left to
 * send the second half, and an opacity-1 black overlay over the next level's
 * loading screen is indistinguishable from a hang — so `'level:loading'` and a
 * new engine both reset to transparent, instantly (duration 0).
 *
 * The node stays MOUNTED at opacity 0 rather than unmounting, because a CSS
 * transition needs a previous computed value to animate from; it is
 * `pointer-events-none` and `aria-hidden` in every state, so at rest it is
 * exactly as inert as not being there.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */

/** Fallback when a payload omits `ms`. Short — a fade is a blink, not a scene. */
const DEFAULT_MS = 220;

export default function FadeOverlay({ engine }) {
  /** @type {[{black: boolean, ms: number}, Function]} */
  const [fade, setFade] = useState({ black: false, ms: 0 });

  useEffect(() => {
    // A new engine means the old world (and any fade it was halfway through)
    // is gone. Instant, so nothing animates a dead level's black away.
    setFade({ black: false, ms: 0 });

    const bus = engine?.bus;
    if (!bus?.on) return undefined;
    const offs = [
      bus.on('screen:fade', (p) => {
        const ms = Number(p?.ms);
        setFade({ black: !!p?.black, ms: Number.isFinite(ms) && ms >= 0 ? ms : DEFAULT_MS });
      }),
      bus.on('level:loading', () => setFade({ black: false, ms: 0 })),
    ];
    return () => {
      for (const off of offs) off?.();
      setFade({ black: false, ms: 0 });
    };
  }, [engine]);

  return (
    <div
      className="pointer-events-none absolute inset-0 z-30 bg-void"
      aria-hidden="true"
      style={{
        opacity: fade.black ? 1 : 0,
        transitionProperty: 'opacity',
        transitionTimingFunction: 'linear',
        transitionDuration: `${fade.ms}ms`,
      }}
    />
  );
}
